import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { GroupMember } from '@olimhub/shared'
import { SYSTEM_BADGE_SLUGS } from '@olimhub/shared'
import { Avatar } from '@/components/profile/Avatar'
import { BadgePill } from '@/components/profile/BadgePill'

interface GroupMemberRowProps {
  member: GroupMember
  groupId: string
  canManageBadges: boolean
  onPromoteToEditor?: (accountId: string) => void
  onPromoteToAdmin?: (accountId: string) => void
  onRevokeBadge?: (accountBadgeId: string) => void
  onAccept?: (accountId: string) => void
  onDecline?: (accountId: string) => void
  working?: boolean
}

export function GroupMemberRow({
  member,
  groupId,
  canManageBadges,
  onPromoteToEditor,
  onPromoteToAdmin,
  onRevokeBadge,
  onAccept,
  onDecline,
  working,
}: GroupMemberRowProps) {
  const { t } = useTranslation()
  const { account } = member

  const roleBadge = account.badges.find((ab) => ab.badge.scope === 'group' && ab.groupId === groupId)
  const isAdmin = roleBadge?.badge.slug === SYSTEM_BADGE_SLUGS.GROUP_ADMIN
  const isEditor = roleBadge?.badge.slug === SYSTEM_BADGE_SLUGS.GROUP_EDITOR

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 16px',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <Link to={`/@${account.username}`} style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0, textDecoration: 'none' }}>
        <Avatar name={account.displayName || account.username} avatarUrl={account.avatarUrl} animalAvatar={account.animalAvatar} accountType={account.accountType} size="sm" />
        <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', fontWeight: 600, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {account.displayName || account.username}
          </span>
          {roleBadge && <BadgePill accountBadge={roleBadge} size="sm" />}
        </div>
      </Link>

      {member.status === 'requested' ? (
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button
            type="button"
            disabled={working}
            onClick={() => onAccept?.(account.id)}
            style={{ padding: '4px 10px', borderRadius: 'var(--radius-pill)', border: 'none', background: 'var(--brand)', color: 'var(--on-brand)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', fontWeight: 600, cursor: working ? 'not-allowed' : 'pointer' }}
          >
            {t('groups.accept')}
          </button>
          <button
            type="button"
            disabled={working}
            onClick={() => onDecline?.(account.id)}
            style={{ padding: '4px 10px', borderRadius: 'var(--radius-pill)', border: '1px solid var(--border)', background: 'none', color: 'var(--text-muted)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', fontWeight: 600, cursor: working ? 'not-allowed' : 'pointer' }}
          >
            {t('groups.decline')}
          </button>
        </div>
      ) : (
        canManageBadges && (
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            {!isAdmin && !isEditor && (
              <button type="button" disabled={working} onClick={() => onPromoteToEditor?.(account.id)} style={pillBtnStyle}>
                {t('groups.make_editor')}
              </button>
            )}
            {isEditor && (
              <>
                <button type="button" disabled={working} onClick={() => onPromoteToAdmin?.(account.id)} style={pillBtnStyle}>
                  {t('groups.make_admin')}
                </button>
                <button type="button" disabled={working} onClick={() => roleBadge && onRevokeBadge?.(roleBadge.id)} style={pillBtnStyleMuted}>
                  {t('groups.remove_editor')}
                </button>
              </>
            )}
            {isAdmin && (
              <button type="button" disabled={working} onClick={() => roleBadge && onRevokeBadge?.(roleBadge.id)} style={pillBtnStyleMuted}>
                {t('groups.remove_admin')}
              </button>
            )}
          </div>
        )
      )}
    </div>
  )
}

const pillBtnStyle: React.CSSProperties = {
  padding: '4px 10px',
  borderRadius: 'var(--radius-pill)',
  border: 'none',
  background: 'var(--brand-soft)',
  color: 'var(--brand)',
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--fs-xs)',
  fontWeight: 600,
  cursor: 'pointer',
}

const pillBtnStyleMuted: React.CSSProperties = {
  ...pillBtnStyle,
  background: 'none',
  border: '1px solid var(--border)',
  color: 'var(--text-muted)',
}
